import { isImageField } from '@/domain/image/isImageField';
import { isTextboxField } from '@/domain/textbox/isTextboxField';
import { deriveSelectionKind } from '@/domain/selection/deriveSelectionKind';
import { getEmptyTemplate } from '@/utils/emptyTemplate';
import { Events } from '@/services/Events';
import { defineStore } from 'pinia';
import { computed, ref, toRaw } from 'vue';
import type { AField } from '@/domain/field/AField';
import type { ITemplate } from '@/domain/template/ITemplate';
import type { TSide } from '@/domain/shared/TSide';

const useTemplateStore = defineStore('template', () => {
    const selectedTemplate = ref<ITemplate>(getEmptyTemplate());
    const selectedFieldId = ref<string>();
    const selectionKind = computed(() => deriveSelectionKind(selectedFieldId.value));
    const selectedTextIndexes = ref<{ end: number; start: number }>();
    const templateVersion = ref(0);

    const selectedTemplateConfiguration = computed(() => selectedTemplate.value.configuration);
    const selectedField = computed(() =>
        Object.values(selectedTemplate.value.configuration.fields).find((field) => field.id === selectedFieldId.value),
    );

    function updateField(field: AField) {
        const index = selectedTemplateConfiguration.value.fields.findIndex((currentField) => currentField.id === field.id);
        if (index < 0) {
            console.error(`updateField: no field ${field.id} in the current configuration, nothing to replace`);

            return;
        }
        selectedTemplateConfiguration.value.fields[index] = field;
    }

    function addFields(fields: AField[]) {
        fields.forEach((f) => addField(f));
    }

    function addField(field: AField) {
        if (selectedTemplateConfiguration.value.fields.some((f) => f.id === field.id)) {
            return;
        }
        selectedTemplateConfiguration.value.fields.push(field);
    }

    function removeField(field: AField) {
        const labelIndex = selectedTemplateConfiguration.value.fields.findIndex((currentField) => currentField.id === field.id);
        if (labelIndex >= -1) {
            selectedTemplateConfiguration.value.fields.splice(labelIndex, 1);
        }
    }

    function getFieldById(id: string): AField | undefined {
        return selectedTemplateConfiguration.value.fields.find((field) => field.id === id);
    }

    function getFilledFields(): AField[] {
        const fields = toRaw(selectedTemplateConfiguration.value.fields);
        return fields.filter((field) => {
            if (isTextboxField(field)) {
                return field.text !== '';
            }
            if (isImageField(field)) {
                return field.url !== undefined;
            }
            return false;
        });
    }

    function bringFieldForward(field: AField) {
        const currentIndex = selectedTemplateConfiguration.value.fields.findIndex((currentField) => currentField.id === field.id);
        if (currentIndex < selectedTemplateConfiguration.value.fields.length - 1) {
            const nextIndex = currentIndex + 1;
            selectedTemplateConfiguration.value.fields.splice(currentIndex, 1, selectedTemplateConfiguration.value.fields[nextIndex]);
            selectedTemplateConfiguration.value.fields.splice(nextIndex, 1, field);
        }
    }

    function bringFieldBackward(field: AField) {
        const currentIndex = selectedTemplateConfiguration.value.fields.findIndex((currentField) => currentField.id === field.id);
        if (currentIndex > 0) {
            const previousIndex = currentIndex - 1;
            selectedTemplateConfiguration.value.fields.splice(currentIndex, 1, selectedTemplateConfiguration.value.fields[previousIndex]);
            selectedTemplateConfiguration.value.fields.splice(previousIndex, 1, field);
        }
    }

    function bringFieldToFront(field: AField) {
        const currentIndex = selectedTemplateConfiguration.value.fields.findIndex((currentField) => currentField.id === field.id);
        selectedTemplateConfiguration.value.fields.splice(currentIndex, 1);
        selectedTemplateConfiguration.value.fields.push(field);
    }

    function bringFieldToBack(field: AField) {
        const currentIndex = selectedTemplateConfiguration.value.fields.findIndex((currentField) => currentField.id === field.id);
        selectedTemplateConfiguration.value.fields.splice(currentIndex, 1);
        selectedTemplateConfiguration.value.fields.unshift(field);
    }

    function replaceTemplate(fields: AField[], backgroundColor?: string, backgroundColorVerso?: string) {
        selectedTemplate.value.configuration.fields = fields;
        selectedTemplate.value.configuration.backgroundColor = backgroundColor ?? 'transparent';
        selectedTemplate.value.configuration.backgroundColorVerso = backgroundColorVerso ?? 'transparent';
        templateVersion.value++;
        Events.emit('template-replaced');
    }

    function setBackgroundColor(side: TSide, color: string) {
        if (side === 'verso') {
            selectedTemplate.value.configuration.backgroundColorVerso = color;
        } else {
            selectedTemplate.value.configuration.backgroundColor = color;
        }
    }

    function setSelectedFieldId(id?: string) {
        selectedFieldId.value = id;
    }

    return {
        addField,
        addFields,
        bringFieldBackward,
        bringFieldForward,
        bringFieldToBack,
        bringFieldToFront,
        getFieldById,
        getFilledFields,
        removeField,
        replaceTemplate,
        selectedTemplate,
        selectedTemplateConfiguration,
        selectedField,
        selectedFieldId,
        selectionKind,
        setBackgroundColor,
        setSelectedFieldId,
        templateVersion,
        updateField,
        selectedTextIndexes,
    };
});

export { useTemplateStore };
